import { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { hasEcommerce, queryProductsDetails } from "../utility/ecommerce";

const formatDDLPrices = (productIds, products, config) => {
  if (products && products.some((product) => product.productSellingPrice)) {
    return productIds.reduce((priceInfo, productId) => {
      const product = products.find((item) => item.key_prod === productId);
      const { productMsrpPrice, productSellingPrice, productOmnibusPrice, shopLink } =
        product || {};

      if (productSellingPrice) {
        priceInfo.set(productId, {
          sellingPrice: {
            value: parseFloat(productSellingPrice.replace(",", ".")),
            currency: config.currency,
          },
          ...(productMsrpPrice && {
            msrpPrice: {
              value: parseFloat(productMsrpPrice.replace(",", ".")),
              currency: config.currency,
            },
          }),
          ...(productOmnibusPrice && {
            omnibusPrice: [
              {
                value: JSON.stringify({
                  amount: productOmnibusPrice,
                  currency_code: config.currency,
                }),
              },
            ],
          }),
          shopLink,
        });
      }
      return priceInfo;
    }, new Map());
  }
  return new Map();
};

const useProductPrices = (productIds, products) => {
  const { config } = useStaticQuery(graphql`
    {
      config: liferayGeneralConfiguration {
        currency
      }
    }
  `);

  const priceInfoFromDDL = useMemo(
    () => formatDDLPrices(productIds, products, config),
    [productIds, products, config]
  );

  const [priceInfo, setPriceInfo] = useState();

  const currency = config.currency;
  const loadPrices = useCallback(async () => {
    if (!productIds.length) return;
    try {
      const items = await queryProductsDetails(productIds, currency);
      const validItems = (items || []).filter((item) => item.availableForSale);
      setPriceInfo(() => {
        const validItemsReduced = validItems.reduce((res, item) => {
          res.set(item.sku, item);
          return res;
        }, new Map(priceInfoFromDDL));

        return validItems?.length > 0 ? validItemsReduced : new Map(priceInfoFromDDL);
      });
    } catch (err) {
      console.error("Error loading prices: ", err);
      setPriceInfo(new Map(priceInfoFromDDL));
    }
  }, [productIds, currency, priceInfoFromDDL]);

  useEffect(() => {
    if (hasEcommerce) {
      loadPrices();
    } else if (priceInfoFromDDL) {
      setPriceInfo(new Map(priceInfoFromDDL));
    }
  }, [loadPrices, priceInfoFromDDL]);

  return priceInfo;
};

export default useProductPrices;
